import { parseAbsoluteToLocal } from "@internationalized/date"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Pass } from "ipctg-inflow-types/InFlowDB"
import { ParkSessionsPOSTSuccessResponseItem } from "ipctg-inflow-types/InFlowParkAPI"
import { AnimatePresence, motion } from "motion/react"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import { PurchasedQuotaPackCard } from "@/globalComponents/molecules/productCards/PurchasedQuotaPack"
import PurchasedSingleSessionCard from "@/globalComponents/molecules/productCards/PurchasedSingleSessionCard"
import { WelcomeNotices } from "@/routes/qr/$qrLookupID/welcome/-components/WelcomeNotices"
import { cleanEmptyParams } from "@/utilities/etc"
import { DEFAULT_ONSITE_HELP_TITLE } from "@/utilities/globals"
import {
  parkSessionsQueryOptions,
  parkValidPassesQueryOptions,
} from "@/utilities/hooks/queries/park"
import { themeQueryOptions } from "@/utilities/hooks/queries/shared"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import useExtendParkSession from "@/utilities/hooks/useExtendParkSession"
import { useTheme } from "@/utilities/hooks/useTheme"
import useToggle from "@/utilities/hooks/useToggle"
import welcomeTokenStorage from "@/utilities/WelcomeTokenStorage"

import Flows from "./-components/Flows"
import SpecialValidationModal from "./-components/SpecialValidationModal"
import WelcomeCard from "./-components/WelcomeCard"

export const Route = createFileRoute("/qr/$qrLookupID/welcome/")({
  staticData: {
    pageName: "Welcome",
  },
  preload: false,
  validateSearch: (
    search: Record<string, unknown>
  ): { redirectFrom?: string } => {
    return cleanEmptyParams({
      redirectFrom: (search.redirectFrom as string) || "",
    })
  },
  beforeLoad: async ({ params, context }) => {
    await api.welcome.POST({ qr: params.qrLookupID })
    const theme = await context.queryClient.ensureQueryData(
      themeQueryOptions({ qrLookupID: params.qrLookupID })
    )
    const flows = theme?.welcome.flowCard.flows || []

    const hasParkFlow = flows.some(
      (f) => f.type === "park" || f.type === "flexPurchase"
    )
    if (hasParkFlow) {
      await Promise.all([
        context.queryClient.ensureQueryData(parkValidPassesQueryOptions()),
        context.queryClient.ensureQueryData(parkSessionsQueryOptions()),
      ])
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation(["welcome"])
  const navigate = useNavigate()
  const { qrLookupID } = Route.useParams()
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const { scannedArea } = useTheme()
  const { setError } = useErrorModal()
  const theme = scannedArea?.theme
  const [showValidationModal, toggleValidationModal] = useToggle()
  const welcomeConfig = theme?.welcome
  const showDeprecatedExtra =
    !welcomeConfig?.belowTheFold &&
    welcomeConfig?.extra === "specialValidation" &&
    theme?.locationInformation?.support.hasOnsiteHelp

  const {
    mutate: extendSession,
    isPending: isExtendSessionPending,
    variables,
  } = useExtendParkSession()

  const flows = theme?.welcome.flowCard.flows || []
  const hasParkFlow = flows.some(
    (f) => f.type === "park" || f.type === "flexPurchase"
  )

  const { data: sessionsData } = useQuery({
    ...parkSessionsQueryOptions(),
    enabled: hasParkFlow,
  })
  const { data: passesData } = useQuery({
    ...parkValidPassesQueryOptions(),
    enabled: hasParkFlow,
  })
  const parkingSessions = sessionsData?.parkingSessions || []
  const passes = passesData?.passes || []

  return (
    <>
      {/* Show welcome text at the top */}
      {getWelcomeText() && (
        <Heading
          className="center"
          level={4}
          as={1}
          style={{ color: "var(--neutral-11)", maxWidth: 350 }}
        >
          {getWelcomeText()}
        </Heading>
      )}
      {!!parkingSessions.length && (
        <div>
          <Heading level={3} style={{ marginBottom: "var(--space-3)" }}>
            {t("dashboard.parkedVehicle", {
              count: parkingSessions.length,
            })}
          </Heading>
          <AnimatePresence>
            {parkingSessions.map(({ canExtend, session }, index) => (
              <motion.div
                key={session.sessionID}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ease: "easeIn", delay: index * 0.1 }}
              >
                <PurchasedSingleSessionCard
                  receiptID={session.transactionIDs[
                    session.transactionIDs.length - 1
                  ].substring(0, 16)}
                  licensePlate={session.plate}
                  sessionStartTime={parseAbsoluteToLocal(session.startDateTime)}
                  sessionEndTime={parseAbsoluteToLocal(session.endDateTime)}
                  extension={{
                    isPending:
                      isExtendSessionPending &&
                      variables.session.sessionID === session.sessionID,
                    canExtend: canExtend,
                    onExtensionPressed: () => {
                      if (
                        isExtendSessionPending &&
                        variables.session.sessionID !== session.sessionID
                      ) {
                        // Don't allow new extensions while an extension is inflight
                        return
                      }
                      handleOnExtendSessionPressed({ canExtend, session })
                    },
                  }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
      {!!passes.length && (
        <div>
          <Heading level={3} style={{ marginBottom: "var(--space-3)" }}>
            {t("dashboard.flexPark", { count: passes.length })}
          </Heading>
          <AnimatePresence>
            {passes.map((pass, index) => (
              <motion.div
                key={pass.passID}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  ease: "easeIn",
                  delay: parkingSessions.length * 0.1 + index * 0.1,
                }}
              >
                <PurchasedQuotaPackCard
                  {...pass}
                  action="redeem"
                  isPending={false}
                  onActionPressed={() => handleOnQuotaPackRedeemPressed(pass)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
      {parkingSessions.length || passes.length ? (
        <div>
          <Heading level={3}>{t("purchase")}</Heading>
          <Flows />
        </div>
      ) : (
        <WelcomeCard authenticated={!!me} qrLookupID={qrLookupID} />
      )}
      {showDeprecatedExtra && (
        <>
          <Text level={1} className="center">
            <Trans
              t={t}
              i18nKey={"belowTheFold.specialValidation"}
              values={{
                onsiteHelperTitle:
                  theme?.locationInformation.support.onsiteHelperTitle ||
                  DEFAULT_ONSITE_HELP_TITLE,
              }}
              components={{
                action: (
                  <Button
                    buttonType="primary"
                    presentation="link"
                    onPress={toggleValidationModal}
                  />
                ),
              }}
            />
          </Text>
          <SpecialValidationModal
            isOpen={showValidationModal}
            onOpenChange={toggleValidationModal}
          />
        </>
      )}
      <WelcomeNotices />
    </>
  )

  // Get the top-level welcome text (e.g., "Welcome to McAllen International Airport")
  function getWelcomeText() {
    if (!theme) return null

    const headerCopyOption = theme.welcome.headerCopyOption
    const headerCopyVariable = theme.welcome.headerCopyVariable
    const locationName = theme.locationInformation.locationName

    if (headerCopyOption === "freeform") {
      // For freeform, use the variable directly without translation, fallback to locationName
      return headerCopyVariable || locationName || null
    } else if (headerCopyOption === "welcomeTo") {
      // For welcomeTo, use translation with the location variable ("Welcome to {{location}}")
      // If no headerCopyVariable, fallback to locationName
      const location = headerCopyVariable || locationName
      return location ? t("welcomeTo", { location }) : null
    }
    return null
  }

  function handleOnExtendSessionPressed(
    sessionItem: ParkSessionsPOSTSuccessResponseItem
  ) {
    if (!me || !sessionItem.canExtend) {
      console.error(
        !me
          ? "Attempting to extend a session without a user present"
          : "Attempting to extend a non extendable session"
      )
      setError({
        error: new Error(t("common.somethingWentWrong", { ns: "errors" })),
      })
      return
    }

    extendSession({
      qrLookupID,
      phone: me.inflowUser.phone,
      email: me.inflowUser.email,
      session: sessionItem.session,
    })
  }

  function handleOnQuotaPackRedeemPressed(pass: Pass) {
    const epoch = welcomeTokenStorage.getEpochTime()
    if (!epoch) {
      console.error(
        "Attempting to redeem a pass without a welcome token with an epoch time stored"
      )
      setError({
        error: new Error(t("common.somethingWentWrong", { ns: "errors" })),
      })
      return
    }
    navigate({
      to: "/qr/$qrLookupID/passes/$epoch/redeem/$passID",
      params: {
        qrLookupID,
        epoch,
        passID: pass.passID,
      },
    })
  }
}
